import { normalizeLocale } from 'src/i18n/config'

const getTranslationByLocale = (translations, locale) => {
  if (!translations || typeof translations !== 'object') {
    return null
  }

  const normalizedLocale = normalizeLocale(locale)

  if (!normalizedLocale) {
    return null
  }

  const directMatch =
    translations?.[locale] || translations?.[normalizedLocale] || translations?.[normalizedLocale.toUpperCase()]

  if (directMatch) {
    return directMatch
  }

  const matchedKey = Object.keys(translations).find((key) => {
    const normalizedKey = normalizeLocale(String(key).split('/').pop())

    return normalizedKey === normalizedLocale
  })

  return matchedKey ? translations?.[matchedKey] || null : null
}

const getEntityWebsiteLanguageCodes = (entity) => {
  if (!entity?.websiteLanguages || typeof entity.websiteLanguages !== 'object') {
    return []
  }

  const entries = Array.isArray(entity.websiteLanguages)
    ? entity.websiteLanguages.map((language) => [null, language])
    : Object.entries(entity.websiteLanguages)

  return entries
    .flatMap(([key, language]) => [
      normalizeLocale(key),
      normalizeLocale(language?.lang?.value),
      normalizeLocale(language?.value),
      normalizeLocale(language?.code),
      normalizeLocale(language?.isoCode),
    ])
    .filter(Boolean)
}

const hasTranslationFieldContent = (translation, preferredFields) => {
  if (!translation) {
    return false
  }

  if (!Array.isArray(preferredFields) || preferredFields.length === 0) {
    return true
  }

  return preferredFields.some((field) => {
    const value = translation?.[field]

    if (typeof value === 'string') {
      return value.trim().length > 0
    }

    if (Array.isArray(value)) {
      return value.length > 0
    }

    return Boolean(value)
  })
}

export const getPreferredTranslation = (
  entity,
  locale,
  {
    fallbackLocale = 'en',
    includeDefaultLang = true,
    includeFirstTranslation = true,
    defaultValue = {},
    preferredFields = [],
  } = {},
) => {
  const translations = entity?.translations

  if (!translations || typeof translations !== 'object') {
    return defaultValue
  }

  const localeTranslation = getTranslationByLocale(translations, locale)
  const defaultLangKey = includeDefaultLang
    ? typeof entity?.defaultLang === 'string'
      ? entity.defaultLang
      : entity?.defaultLang?.value
    : null
  const defaultLangTranslation = defaultLangKey ? getTranslationByLocale(translations, defaultLangKey) : null
  const websiteLanguageCodes = getEntityWebsiteLanguageCodes(entity)
  const shouldDeprioritizeLocale =
    websiteLanguageCodes.length > 0 && !websiteLanguageCodes.includes(normalizeLocale(locale))
  const fallbackTranslation = fallbackLocale ? getTranslationByLocale(translations, fallbackLocale) : null
  const firstTranslation = includeFirstTranslation
    ? Object.values(translations).find((translation) =>
        hasTranslationFieldContent(translation, preferredFields),
      ) || null
    : null
  const candidates = [
    shouldDeprioritizeLocale ? null : localeTranslation,
    defaultLangTranslation,
    shouldDeprioritizeLocale ? localeTranslation : null,
    fallbackTranslation,
    firstTranslation,
  ]

  return candidates.find((translation) => hasTranslationFieldContent(translation, preferredFields)) || defaultValue
}
